/**
 * **Map geometry for the effects engine.** Footprint distance ({@link combatantRange}), line of
 * sight ({@link combatantLineOfSight}), and the movement helpers below. The geometry predicate
 * ops (`adjacent_to`, `range_to`, `is_closest_hostile`, `exists_ally`) read the map through these
 * directly. (This module used to pre-answer a recorded-boolean `spatial` op off the map; that op
 * became `gm_ruling` for facts the map can't answer, `decisions/0183`.)
 *
 * The **forced-move destination** ({@link pushDestination}) is the other half: given a
 * displacement distance and the combatant it is pushed away from, walk the line
 * directly away (over the spatial port) and stop at the map edge / a solid blocker. This is what the
 * `forced_move` atom records into its `forced_move` step (Knockback's positional push).
 */

import {
  type Cell,
  type EncounterState,
  type Combatant,
  type ForcedMoveCollision,
  type World,
  BLINDED_STATUS,
  endpointFitterIn,
  hasStatus,
  isCharacter,
  movementContext,
  terrainVisibility,
  worldFor,
  encounterGridMode,
  tagCell,
} from "@lancer-vtt/domain";
import { cellRegion, footprintFits, movePathCost, reachableAnchors } from "@lancer-vtt/space";

/**
 * The footprint cells a placed combatant occupies (anchor + Size); empty if unplaced.
 * Exported so the set-returning area-query seam ({@link import("./spatialQuery").enumerateArea})
 * intersects template regions against the **same** footprint {@link combatantRange} measures
 * from: one definition of "the cells a combatant covers."
 */
export function footprintCells(world: World<Cell>, c: Combatant | undefined): Cell[] {
  if (!c || !c.pos) return [];
  return world.footprint(c.pos, c.base.size).cells();
}

/**
 * Minimum grid distance between two footprints — the port's `regionDistance` (B2)
 * over the cells, **not** a hand-rolled cross-product on raw coordinates. `null`
 * when either footprint is empty (the world returns `Infinity`, the "no answer"
 * sentinel, which we map to `null`).
 *
 * **Exported** so a caller that needs a footprint measured
 * from a *hypothetical* anchor (not a placed combatant's live `pos`) can build that
 * footprint itself (`world.footprint(cell, size).cells()`) and still reuse this same
 * distance primitive — `mine.ts`'s trigger sweep does this to measure a mover's
 * pre-move `from` cell against a mine, since {@link combatantRange} only reads a
 * combatant's CURRENT stored `pos`.
 */
export function footprintDistance(world: World<Cell>, a: Cell[], b: Cell[]): number | null {
  if (a.length === 0 || b.length === 0) return null;
  const d = world.regionDistance(cellRegion(a, world.key), cellRegion(b, world.key));
  return Number.isFinite(d) ? d : null;
}

/**
 * The **range to target** between two combatants by id — the min `@lancer-vtt/space`
 * distance between their Size footprints (A5 Overwatch's threat-range check; the same
 * footprint-to-footprint metric the spatial `range` predicate uses). `null` when either
 * combatant is unknown or unplaced (no map position to measure from).
 */
export function combatantRange(
  state: EncounterState,
  a: string,
  b: string,
): number | null {
  const world = worldFor(state);
  return footprintDistance(
    world,
    footprintCells(world, state.combatants[a]),
    footprintCells(world, state.combatants[b]),
  );
}

/**
 * What cuts `viewer`'s sight to `to` before the terrain is consulted, as a reason, or `undefined`:
 * - **Blinded** ({@link BLINDED_STATUS}, the Flash Mine): the viewer sees only adjacent spaces.
 * - **A `blind_out` area** (the Flash Grenade): a viewer other than the area's source, at least
 *   partly inside it, can't see a target with no space inside it.
 * The one read every combatant-drawn LoS check folds in.
 */
export function sightCut(state: EncounterState, viewer: Combatant, to: readonly Cell[]): string | undefined {
  const blinded = hasStatus(viewer.statuses, BLINDED_STATUS);
  const zones = Object.values(state.areaEffects ?? {}).filter((a) => a.grants === "blind_out" && a.source !== viewer.id);
  if (!blinded && zones.length === 0) return undefined; // the common case reads nothing spatial
  const world = worldFor(state);
  const from = footprintCells(world, viewer);
  if (blinded) {
    const d = footprintDistance(world, from, [...to]);
    if (d === null || d > 1) return `${viewer.name} is Blinded — no line of sight past adjacent spaces`;
  }
  for (const area of zones) {
    const inside = new Set(world.disk(area.origin, area.size).cells().map((c) => world.key(c)));
    if (from.some((c) => inside.has(world.key(c))) && !to.some((c) => inside.has(world.key(c)))) {
      return `${viewer.name} is inside a zone of blinding light — no line of sight out of it`;
    }
  }
  return undefined;
}

/**
 * **Line of sight between two combatants** (`reactive-defenses-content` P3 — Intercede's
 * protector-gather condition-gate, "a source within your line of sight"; also usable by a
 * future targeting-legality gate) — an unblocked sightline between the two footprints, via the
 * SAME `terrainVisibility` geometry the `line_of_sight` spatial predicate and `search.ts`'s
 * `checkSearchLoS` already reuse (never reimplement it). `false` when either combatant is
 * unknown/unplaced (no footprint to measure from) — the honest no-answer default, mirroring
 * every other spatial-answerer read here.
 */
export function combatantLineOfSight(state: EncounterState, a: string, b: string): boolean {
  const ca = state.combatants[a];
  const cb = state.combatants[b];
  if (!ca?.pos || !cb?.pos) return false;
  const world = worldFor(state);
  const from = footprintCells(world, ca);
  const to = footprintCells(world, cb);
  if (sightCut(state, ca, to) !== undefined) return false;
  return terrainVisibility({ space: world, terrain: state.terrain, from, to, edges: state.edges, combatants: state.combatants }).visible;
}

/**
 * {@link combatantLineOfSight} to a bare cell: an unblocked sightline from `a`'s footprint to
 * `cell` (a mine's or a hazard's space). `false` when `a` is unknown or unplaced.
 */
export function combatantCellLineOfSight(state: EncounterState, a: string, cell: Cell): boolean {
  const ca = state.combatants[a];
  if (!ca?.pos) return false;
  if (sightCut(state, ca, [cell]) !== undefined) return false;
  const world = worldFor(state);
  return terrainVisibility({ space: world, terrain: state.terrain, from: footprintCells(world, ca), to: [cell], edges: state.edges, combatants: state.combatants }).visible;
}

/**
 * The **range to a bare cell** from a combatant's footprint — the min `@lancer-vtt/space`
 * distance between the attacker's Size footprint and the single targeted cell (the
 * area/cell-origin gate). The cell analogue of {@link combatantRange}: where that
 * measures footprint-to-footprint between two units, this measures footprint-to-cell so a
 * cell-targeted attack (an AoE origin, or a single-target shot aimed at empty ground) can be
 * range-gated before the template fans out. `null` when the combatant is unknown/unplaced (no
 * footprint to measure from). The cell is treated as a one-cell region.
 */
export function combatantCellRange(
  state: EncounterState,
  combatantId: string,
  cell: Cell,
): number | null {
  const world = worldFor(state);
  return footprintDistance(world, footprintCells(world, state.combatants[combatantId]), [cell]);
}

// ── the forced-move destination (the push geometry) ───────────────────────────────

/**
 * The cell a target ends on after a forced displacement of `dist` spaces **directly
 * away** from `awayFromId`, walked one hex step at a time and **stopped** at the map
 * bound (`mapRadius`) or a solid cell — **the same mover-relative, footprint-aware
 * predicate `move_token`/the overlay use**, not a
 * bare terrain-only gate: an equal-or-greater hostile character, an object/deployable,
 * or terrain-solid stops the ray outright; an ally or a strictly-smaller hostile never
 * stops it (RAW p.62: involuntary movement is "still blocked by obstructions," and the
 * same OBSTRUCTION Size/side rule governs it). Returns `null` when the geometry can't
 * run (either combatant unplaced, or they share a cell so there's no away-direction) —
 * the caller records `to: null` (an honest no-op) and surfaces the deferral, never a
 * faked destination.
 *
 * **Path-vs-endpoint, same as movement.** The ray keeps walking through `noStop` cells
 * (an ally, a smaller hostile) — they never obstruct travel — but the target can't
 * *end* the push overlapping one (RAW: "can't end a movement in a space occupied by
 * another character or object," involuntary movement included). So the last cell the
 * footprint could legally **stop** on (`footprintEndpointFits` — `blocked ∪ noStop`) is
 * tracked separately from the cell the ray has walked to, and the push **backs off** to
 * it when the ray runs out of distance or is stopped mid-`noStop`: allies/smaller
 * hostiles never stop the ray, but the target can't land on top of them either. A push
 * that makes zero progress (blocked on the very first step) records the original cell.
 *
 * Single-cell anchor model (matches `move`/`move_token`): the target's `pos` anchor is
 * displaced; the footprint is derived from `(pos, size)` as everywhere else. The
 * occupancy is classified **relative to the pushed target** (`movementContext`'s
 * `exceptId` is `targetId`, not the pusher) — an ally of the target passes it by, a
 * combatant hostile to the target obstructs by the ordinary Size rule, exactly as if
 * the target were walking under its own power.
 *
 * **Engagement recompute is NOT this seam's job.** The recomputer chain runs against a
 * resolution's own `forced_move` steps (not `move_token` alone) via a post-resolution followup
 * in `backend/src/encounterRoom.ts`.
 *
 * **Engine policy over port primitives.** The push is a Knockback
 * *rule*, not geometry, so it lives engine-side — but it is now expressed purely over
 * the spatial port (`neighbors`/`line`/`distance`/`equals`/`inBounds`), never raw
 * coordinate algebra (`subtract`/`add`/`scale`) or a position literal. Each step walks
 * to the neighbour of the current cell that continues the straight ray *directly away*
 * from the source: the one neighbour that is one space farther from the source **and**
 * collinear (the source→neighbour line passes through the current cell). That is the
 * port-primitive expression of "step straight away," grid-agnostic by construction.
 */
export type PushDestinationArgs = {
  state: EncounterState;
  targetId: string;
  dist: number;
  /** Push directly away from this **combatant**'s cell (the source of the knock) — the usual Knockback. */
  awayFromId?: string;
  /**
   * OR push directly away from a fixed **cell** — the area template center (a Blast-Knockback
   * weapon's `away_from: "site"` pushes every target away from the blast center, not from the
   * attacker). Takes precedence over `awayFromId` when both are supplied.
   */
  awayFromCell?: Cell;
};

export function pushDestination(args: PushDestinationArgs): Cell | null {
  return pushDestinationWithCollision(args)?.to ?? null;
}

/**
 * {@link pushDestination} plus why the push stopped short, if it did ({@link ForcedMoveCollision}).
 * The collision space is the first one the walk entered but could not end on, or else the
 * blocked space that stopped it; a push that ran its distance or stopped at the map edge has none.
 */
export function pushDestinationWithCollision(args: PushDestinationArgs): { to: Cell; collision?: ForcedMoveCollision } | null {
  const { state, targetId, dist } = args;
  const target = state.combatants[targetId];
  // The source cell to push away from: a fixed template-center cell wins, else the named combatant's cell.
  const src = args.awayFromCell ?? (args.awayFromId ? state.combatants[args.awayFromId]?.pos ?? null : null);
  if (!target?.pos || !src) return null;
  if (dist <= 0) return { to: target.pos };

  const world = worldFor(state);
  const start = target.pos;
  if (world.equals(start, src)) return null; // same cell — no away-direction.

  // The SAME mover-relative occupancy `move_token`/the overlay validate against
  // (`packages/domain/src/encounter/movement.ts`) — classified relative to the
  // PUSHED unit (`targetId`), not the pusher, so an ally of the target passes it
  // by and a hostile obstructs it by the ordinary Size rule.
  const ctx = movementContext(world, state, targetId);
  const size = target.base.size;
  const fitArgs = { space: world, size, blocked: ctx.blocked, inBounds: ctx.inBounds };
  const endpointLegal = endpointFitterIn(world, ctx, size);

  // The walk-and-backoff loop itself is the SHARED core with the grapple drag — see
  // {@link walkAndBackoff}. Here each candidate
  // cell is generated lazily, one at a time, by stepping `awayStep` off the last
  // ACCEPTED cell (`cur`); the drag instead hands a precomputed `world.line` sequence.
  const walk = walkAndBackoffTrace({
    world,
    fitArgs,
    endpointLegal,
    start,
    maxSteps: dist,
    next: (cur) => awayStep(world, src, cur),
  });
  // Tag the result with the encounter's grid: the destination is stored as a `pos`
  // (a `forced_move` step), so it must be a self-describing tagged `Cell` like every
  // other driver/wire cell — geometry-derived cells (`world.neighbors`/`line`) are
  // tag-free at runtime, so stamp it here at the engine→state boundary.
  const mode = encounterGridMode(state);
  const to = tagCell(mode, walk.landing);
  const landedAt = walk.walked.findIndex((c) => world.equals(c, walk.landing));
  const at = walk.walked[landedAt + 1] ?? walk.blocked;
  if (at === undefined) return { to };
  const atKeys = new Set(world.footprint(at, size).cells().map((c) => world.key(c)));
  const occupants = Object.values(state.combatants)
    .filter((c) => c.id !== targetId && isCharacter(c) && footprintCells(world, c).some((cell) => atKeys.has(world.key(cell))))
    .map((c) => c.id)
    .sort();
  return { to, collision: { at: tagCell(mode, at), occupants, obstructed: walk.blocked !== undefined } };
}

/** Named arguments for {@link nearestFreeAnchor}. */
export type NearestFreeAnchorArgs = {
  state: EncounterState;
  /** The unit to place. Its current `pos` is the search origin and its `base.size` the footprint that must fit. */
  targetId: string;
  /** Extra cell keys the footprint may not overlap, on top of the unit's own occupancy context (e.g. a footprint that is about to be occupied). */
  avoidKeys?: ReadonlySet<string>;
  /**
   * Tie-breaks, in order: among equally near anchors, prefer the one whose footprint is FARTHEST
   * from the first cell set; still tied, from the second; and so on.
   */
  awayFrom?: readonly (readonly Cell[])[];
};

/**
 * The nearest anchor where `targetId`'s footprint may legally END a movement — RAW's "the nearest
 * free space" (Lesson of the Held Image p.191, Blinkshield p.241). It is a placement, not a walk:
 * candidates are every in-bounds anchor, and nothing between the origin and the answer has to be
 * passable.
 *
 * **"Free" is the endpoint legality every forced move here uses**: {@link movementContext}
 * relative to the placed unit plus {@link endpointFitterIn} (`blocked ∪ noStop`), with
 * `avoidKeys` added to `blocked`. So a hostile, an ally, an object and solid terrain all rule a
 * cell out, exactly as they would for a push landing.
 *
 * **Order, fully deterministic:** (1) smallest grid distance (`world.distance`) from the unit's
 * current anchor; (2) largest footprint distance from each `awayFrom` cell set in turn; (3)
 * smallest `world.key` in string order. A legal current anchor answers itself (distance 0). Returns `null` when the
 * unit is unplaced or no in-bounds anchor fits.
 */
export function nearestFreeAnchor(args: NearestFreeAnchorArgs): Cell | null {
  const { state, targetId, avoidKeys, awayFrom = [] } = args;
  const target = state.combatants[targetId];
  if (!target?.pos) return null;
  const world = worldFor(state);
  const start = target.pos;
  const size = target.base.size;
  const ctx = movementContext(world, state, targetId);
  const blocked = avoidKeys === undefined ? ctx.blocked : new Set([...ctx.blocked, ...avoidKeys]);
  const legal = endpointFitterIn(world, { ...ctx, blocked }, size);
  const awayDistances = (anchor: Cell) => {
    const cells = world.footprint(anchor, size).cells();
    return awayFrom.map((source) => footprintDistance(world, cells, [...source]) ?? 0);
  };

  // Flood every in-bounds cell from the origin (the map is always bounded), then rank.
  let best: FreeAnchorCandidate | undefined;
  const seen = new Set<string>([world.key(start)]);
  const queue: Cell[] = [start];
  for (let i = 0; i < queue.length; i++) {
    const cell = queue[i]!;
    for (const n of world.neighbors(cell)) {
      const key = world.key(n);
      if (seen.has(key) || !world.inBounds(n)) continue;
      seen.add(key);
      queue.push(n);
    }
    if (!legal(cell)) continue;
    const candidate = { cell, d: world.distance(start, cell), away: awayDistances(cell), key: world.key(cell) };
    if (best === undefined || ranksBefore(candidate, best)) best = candidate;
  }
  return best === undefined ? null : tagCell(encounterGridMode(state), best.cell);
}

type FreeAnchorCandidate = { cell: Cell; d: number; away: number[]; key: string };

function ranksBefore(a: FreeAnchorCandidate, b: FreeAnchorCandidate): boolean {
  if (a.d !== b.d) return a.d < b.d;
  for (let i = 0; i < a.away.length; i++) {
    if (a.away[i] !== b.away[i]) return a.away[i]! > b.away[i]!;
  }
  return a.key < b.key;
}

/**
 * The cell a mover ends on after a **voluntary** displacement of up to `dist` spaces
 * TOWARD a fixed destination cell (the `move_self_toward_target` atom —
 * t_hunter:1's Lunge "fly up to 3 spaces directly towards the targeted character," t_ace:1's
 * Acrobatics "fly up to 2 spaces in any direction"). The structural MIRROR of
 * {@link pushDestination} (which walks AWAY from a source, involuntary) — this walks TOWARD a
 * destination, reusing the exact SAME shared core ({@link walkAndBackoff}) the grapple drag
 * recomputer (`dragRecompute.ts`) already proved for a "walk toward a fixed cell" geometry, via
 * a precomputed `world.line(mover.pos, towardCell)` sequence (not `pushDestination`'s own lazy
 * `awayStep` generator, which only knows how to step AWAY). Occupancy is classified relative to
 * the MOVER (the one being displaced) — the same `movementContext`/`footprintEndpointFits`
 * legality every other forced-move geometry in this file uses (an equal-or-greater hostile
 * blocks the ray outright; an ally/smaller hostile is traversable but not endable, so the walk
 * naturally stops short of *landing on* the target rather than needing a bespoke "don't end on
 * the target" carve-out). Returns `null` when the mover is unplaced or `towardCell` coincides
 * with the mover's own cell (no travel direction to walk) — the caller records an honest no-op
 * displacement (`to: null`) in THOSE cases. A fully-blocked walk (obstructed on the very first
 * candidate) is a DIFFERENT case: it returns the mover's own cell (a real, legitimate "you tried
 * to move but were blocked outright" landing), mirroring {@link pushDestination}'s own
 * documented zero-progress convention (`walkAndBackoff`'s own doc) — never `null`.
 */
export type TravelDestinationArgs = {
  state: EncounterState;
  moverId: string;
  dist: number;
  /** The fixed cell to travel toward — a resolved combatant's position, OR a player-chosen destination. */
  towardCell: Cell;
  /**
   * **The magnitude clamp (`upTo`)** — a player-answered cell constraining how far along the
   * `mover → towardCell` ray the walk actually goes, layered ON TOP of the direction (`towardCell`
   * stays engine-derived — this never changes WHERE the ray points, only how much of it gets
   * walked). Projected onto the ray by NEAREST-CELL match, never exact-match: the ray index whose
   * cell is closest (grid distance) to `upToCell` becomes the cap (`index + 1`), so an off-ray
   * pick clamps to the nearest ray cell instead of being rejected outright (a GM mid-encounter
   * must not get an error for a one-cell-off click). Combined with `dist`/`rayCells.length` via
   * `Math.min` — never a WIDER magnitude than the authored `dist` ceiling. Omitted →
   * `maxSteps = min(dist, rayCells.length)`.
   */
  upToCell?: Cell;
  /**
   * **The per-walk flight override** — threads straight into {@link movementContext}'s
   * `{ exceptId, as }` spec form so THIS walk flies (ignores ordinary edge walls + solid terrain
   * + occupants) without stamping `FLYING_STATUS` onto the mover.
   * Omitted → ordinary status-based flight detection only.
   */
  as?: "flying";
  /**
   * The combatant the walk is pulled toward, when it is one. Its own footprint is where a pull
   * naturally ends, so reaching it is not a collision ({@link travelDestinationWithCollision}).
   */
  anchorId?: string;
};

/**
 * Occupant pass-through follows the mover's statuses via {@link movementContext}
 * (`packages/domain/src/encounter/movement.ts` tier 1a): only a `TRAMPLING_STATUS` mover crosses a
 * character. The Cataphract gets that status from a turn-start lifecycle trigger, so a golden or
 * proof that calls `resolveActivate` directly must add the status itself, or
 * `move_self_toward_target` stops one cell short of the occupant with no error.
 */
export function travelDestination(args: TravelDestinationArgs): Cell | null {
  return travelDestinationWithCollision(args)?.to ?? null;
}

/**
 * {@link travelDestination} plus why the walk stopped short, if it did, in
 * {@link pushDestinationWithCollision}'s shape: the Gravity Rifle's "if they collide with an
 * obstruction, they are additionally knocked Prone" reads it through the `collided` predicate. A walk
 * that ran its distance, stopped at the map edge, or stopped against {@link TravelDestinationArgs.anchorId}'s
 * own footprint (the puller it reached) has none.
 */
export function travelDestinationWithCollision(args: TravelDestinationArgs): { to: Cell; collision?: ForcedMoveCollision } | null {
  const { state, moverId, dist, towardCell, upToCell, as, anchorId } = args;
  const mover = state.combatants[moverId];
  if (!mover?.pos || dist <= 0) return null;

  const world = worldFor(state);
  if (world.equals(mover.pos, towardCell)) return null; // already there — no travel direction.

  // Occupancy classified relative to the MOVER (`exceptId: moverId`) — same discipline
  // `pushDestination`/the grapple drag use, each relative to whichever combatant is displaced.
  // `as` threads through to `movementContext`'s per-walk flight override — this walk flies
  // without stamping a status on the mover.
  const ctx = movementContext(world, state, as !== undefined ? { exceptId: moverId, as } : moverId);
  const size = mover.base.size;
  const fitArgs = { space: world, size, blocked: ctx.blocked, inBounds: ctx.inBounds };
  const endpointLegal = endpointFitterIn(world, ctx, size);

  // A precomputed straight-line sequence toward `towardCell` (mirrors the grapple drag's own
  // `world.line(partner.pos, ideal).slice(1)`) — `walkAndBackoff`'s `next` just indexes into it,
  // never re-deriving a direction step (unlike `pushDestination`'s away-only `awayStep`).
  const rayCells = world.line(mover.pos, towardCell).slice(1);
  let maxSteps = Math.min(dist, rayCells.length);
  if (upToCell !== undefined && rayCells.length > 0) {
    // Project `upToCell` onto the ray by nearest-cell match (never exact-match-or-reject).
    let bestIndex = 0;
    let bestDistance = world.distance(rayCells[0]!, upToCell);
    for (let i = 1; i < rayCells.length; i++) {
      const d = world.distance(rayCells[i]!, upToCell);
      if (d < bestDistance) {
        bestDistance = d;
        bestIndex = i;
      }
    }
    maxSteps = Math.min(maxSteps, bestIndex + 1);
  }
  const walk = walkAndBackoffTrace({
    world,
    fitArgs,
    endpointLegal,
    start: mover.pos,
    maxSteps,
    next: (_cur, i) => rayCells[i] ?? null,
  });
  // Mirrors `pushDestination`'s own convention (`walkAndBackoff`'s doc): a fully-blocked walk
  // (obstructed on the very first candidate) returns `start` itself, NOT `null` — the caller
  // still gets a real cell (a legitimate "you tried to move but a wall stopped you outright"
  // record), `null` is reserved for "no geometry could even be attempted" (unplaced/no anchor).
  const mode = encounterGridMode(state);
  const to = tagCell(mode, walk.landing);
  const landedAt = walk.walked.findIndex((c) => world.equals(c, walk.landing));
  const at = walk.walked[landedAt + 1] ?? walk.blocked;
  if (at === undefined) return { to };
  const atKeys = new Set(world.footprint(at, size).cells().map((c) => world.key(c)));
  const anchor = anchorId === undefined ? undefined : state.combatants[anchorId];
  if (anchor?.pos && footprintCells(world, anchor).some((cell) => atKeys.has(world.key(cell)))) return { to };
  const occupants = Object.values(state.combatants)
    .filter((c) => c.id !== moverId && isCharacter(c) && footprintCells(world, c).some((cell) => atKeys.has(world.key(cell))))
    .map((c) => c.id)
    .sort();
  return { to, collision: { at: tagCell(mode, at), occupants, obstructed: walk.blocked !== undefined } };
}

/**
 * **The paintable "where can this magnitude pick land" set** — the distinct legal landing
 * cells for {@link travelDestination}'s magnitude clamp,
 * for every magnitude `1..min(dist, rayCells.length)` along the SAME fixed ray
 * (`world.line(mover.pos, towardCell)`) `travelDestination` itself walks. Reuses
 * {@link walkAndBackoff} verbatim, once per candidate magnitude, rather than re-deriving the
 * legality rule — so what gets painted can never drift from what the engine actually accepts.
 * (A naive radius would show cells the engine would later clamp away.)
 *
 * **Only meaningful for the fixed-direction shape** — `towardCell` is engine-derived (Hunter's
 * `toward:"target"` + `upTo:true`: the direction is locked, only the MAGNITUDE is picked). A
 * `toward:"chosen"` clause (Siege Specialist, Ace) has no anchor to draw a ray toward BEFORE the
 * player picks one — there is no analogous "reachable cells" set to precompute, and the one call
 * site ({@link import("./interpret").interpretMoveSelfTowardTarget}) never calls this for that
 * shape (see its own comment). That mode keeps a bare crosshair instead.
 */
export function travelReachableCells(args: Omit<TravelDestinationArgs, "upToCell">): Cell[] {
  const { state, moverId, dist, towardCell, as } = args;
  const mover = state.combatants[moverId];
  if (!mover?.pos || dist <= 0) return [];

  const world = worldFor(state);
  if (world.equals(mover.pos, towardCell)) return [];

  const ctx = movementContext(world, state, as !== undefined ? { exceptId: moverId, as } : moverId);
  const size = mover.base.size;
  const fitArgs = { space: world, size, blocked: ctx.blocked, inBounds: ctx.inBounds };
  const endpointLegal = endpointFitterIn(world, ctx, size);

  const rayCells = world.line(mover.pos, towardCell).slice(1);
  const maxSteps = Math.min(dist, rayCells.length);

  const seen = new Set<string>();
  const out: Cell[] = [];
  for (let steps = 1; steps <= maxSteps; steps++) {
    const landing = walkAndBackoff({
      world,
      fitArgs,
      endpointLegal,
      start: mover.pos,
      maxSteps: steps,
      next: (_cur, i) => rayCells[i] ?? null,
    });
    if (world.equals(landing, mover.pos)) continue; // zero progress at this magnitude — not a real cell to paint.
    const key = world.key(landing);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(tagCell(encounterGridMode(state), landing));
  }
  return out;
}

/**
 * **A straight-line walk under the movement rules** (`move_self_toward_target { asMove }`, Puppet
 * System's ruling 9): the mover heads from its cell through `headingCell` and on past it, spending
 * `budget` on the same per-step cost a `move_token` pays (difficult terrain, a Prone crawl). It
 * stops outright at an obstruction or the map edge, ends on the step that engages it with a
 * character of equal or greater Size (the movement context's `stopsAt`), and backs off to the last
 * cell its footprint may end on. Returns the walked anchors, `from` first and the landing last, or
 * `null` when the mover is unplaced or `headingCell` is its own cell.
 */
export function moveWalk(args: { state: EncounterState; moverId: string; budget: number; headingCell: Cell }): Cell[] | null {
  const { state, moverId, budget, headingCell } = args;
  const mover = state.combatants[moverId];
  if (!mover?.pos || budget <= 0) return null;
  const world = worldFor(state);
  if (world.equals(mover.pos, headingCell)) return null;
  const ctx = movementContext(world, state, moverId);
  // Entering a cell is judged without the endpoint rule (`noStop`/`endpointOk`); a stop is judged apart.
  const enterOnly = { ...ctx, noStop: undefined, endpointOk: undefined };
  const size = mover.base.size;
  const mayEnd = endpointFitterIn(world, ctx, size);
  // Each step costs at least 1, so the line never needs more than `budget` cells.
  let far = headingCell;
  while (world.distance(mover.pos, far) < budget) far = world.translate(far, mover.pos, headingCell);
  const ray = world.line(mover.pos, far).slice(1);
  const walked: Cell[] = [mover.pos];
  let landing = 0;
  let spent = 0;
  for (const next of ray) {
    const step = movePathCost({ space: world, path: [walked.at(-1)!, next], size, ctx: enterOnly });
    if (!step.ok || !world.inBounds(next) || spent + step.cost > budget) break;
    spent += step.cost;
    walked.push(next);
    if (mayEnd(next)) landing = walked.length - 1;
    if (step.stopped) break;
  }
  return walked.slice(0, landing + 1).map((c) => tagCell(encounterGridMode(state), c));
}

/**
 * **The shared walk-and-backoff loop** {@link pushDestination} (Knockback) and the
 * grapple drag recomputer both need — factored out here so the two forced-move geometries
 * can't drift apart. Walks up to
 * `maxSteps` candidate cells, each produced by `next(cur, i)` from the **last accepted**
 * cell `cur` and the 0-based step index `i` (`pushDestination` derives each lazily via
 * `awayStep` off a straight ray; the drag hands a precomputed `world.line` sequence
 * indexed by `i`, ignoring `cur`). At each candidate: a `null` from `next`, a cell outside
 * `world.inBounds`, or one that fails `footprintFits` (a `blocked` cell — an
 * equal-or-greater hostile, a genuinely-blocking object, terrain-solid) stops the walk
 * outright WITHOUT advancing `cur`; otherwise `cur` advances to it, and if it ALSO passes
 * `endpointLegal` (footprint-legal to stop on — `blocked ∪ noStop`), it becomes the new
 * best legal landing. Returns the best legal landing, defaulting to `start` when the walk
 * makes zero legal progress (blocked on the very first candidate records the original cell).
 */
type WalkArgs = {
  world: World<Cell>;
  fitArgs: { space: World<Cell>; size: number; blocked: Set<string>; inBounds: (p: Cell) => boolean };
  endpointLegal: (anchor: Cell) => boolean;
  start: Cell;
  maxSteps: number;
  next: (cur: Cell, i: number) => Cell | null;
};

export function walkAndBackoff(args: WalkArgs): Cell {
  return walkAndBackoffTrace(args).landing;
}

/**
 * {@link walkAndBackoff} with its trace: every cell the walk entered (`start` first) and the
 * blocked candidate that stopped it outright, if one did (the map edge and a `null` continuation
 * are not blocks).
 */
function walkAndBackoffTrace(args: WalkArgs): { landing: Cell; walked: Cell[]; blocked?: Cell } {
  const { world, fitArgs, endpointLegal, start, maxSteps, next } = args;
  let cur = start;
  const walked = [start];
  // The last cell the footprint could legally STOP on. Tracked separately from `cur`:
  // the walk keeps advancing through `noStop` occupants (an ally, a smaller hostile) it
  // can travel over but can't end on top of, so when the walk is stopped mid-`noStop` or
  // simply runs out of steps, it backs off to this cell instead of the walk's true tip.
  let lastLegal = start;
  for (let i = 0; i < maxSteps; i++) {
    const candidate = next(cur, i);
    if (candidate === null) break; // no continuation (e.g. off the lattice frontier).
    if (!world.inBounds(candidate)) break; // stop at the map bound.
    if (!footprintFits({ ...fitArgs, anchor: candidate })) return { landing: lastLegal, walked, blocked: candidate }; // stop outright at a blocked cell.
    cur = candidate;
    walked.push(cur);
    if (endpointLegal(cur)) lastLegal = cur;
  }
  return { landing: lastLegal, walked };
}

/**
 * **The nearest legal spaces adjacent to a footprint** — every anchor within a bounded disk of
 * `anchor` that is a legal endpoint for a `draggeeSize` footprint (`endpointLegal`) AND
 * footprint-adjacent to the `anchorSize` footprint at `anchor`, keeping only those at the minimal
 * grid distance from `near`, sorted by `world.key` ascending. `[]` when no such cell exists.
 *
 * Shared by the grapple drag's adjacency-restore fallback (`dragRecompute.ts`, which takes the
 * first) and `place_adjacent` (`interpret.ts`, Impale's pull, which prompts when more than one
 * ties — Open fork F4). The radius bound: a footprint-adjacent anchor lies at most
 * `anchorSize + draggeeSize + 1` cells from `anchor`.
 */
export function nearestAdjacentLandings(args: {
  world: World<Cell>;
  anchor: Cell;
  anchorSize: number;
  draggeeSize: number;
  near: Cell;
  endpointLegal: (anchor: Cell) => boolean;
}): Cell[] {
  const { world, anchor, anchorSize, draggeeSize, near, endpointLegal } = args;
  const anchorFp = cellRegion(world.footprint(anchor, anchorSize).cells(), world.key);
  const radius = Math.ceil(anchorSize) + Math.ceil(draggeeSize) + 1;
  let best: Cell[] = [];
  let bestDist = Infinity;
  for (const candidate of world.disk(anchor, radius).cells()) {
    if (!endpointLegal(candidate)) continue;
    const fp = cellRegion(world.footprint(candidate, draggeeSize).cells(), world.key);
    if (world.regionDistance(anchorFp, fp) !== 1) continue;
    const d = world.distance(candidate, near);
    if (d < bestDist) {
      best = [candidate];
      bestDist = d;
    } else if (d === bestDist) {
      best.push(candidate);
    }
  }
  return best.sort((a, b) => (world.key(a) < world.key(b) ? -1 : world.key(a) > world.key(b) ? 1 : 0));
}

/**
 * The single neighbour of `cur` that continues the straight ray **directly away** from
 * `source` — one space farther from `source` and collinear (the `source`→neighbour line
 * runs through `cur`). Returns `null` if no such neighbour exists. Pure port primitives:
 * the engine never reads a coordinate or constructs a position.
 */
function awayStep(world: World<Cell>, source: Cell, cur: Cell): Cell | null {
  const dCur = world.distance(source, cur);
  for (const n of world.neighbors(cur)) {
    if (world.distance(source, n) !== dCur + 1) continue;
    // Collinear: the straight line from `source` to `n` passes through `cur`.
    if (world.line(source, n).some((c) => world.equals(c, cur))) return n;
  }
  return null;
}

/**
 * The anchors `moverId` can move to within `dist` spaces under ordinary movement rules (walls,
 * terrain, occupants), its own anchor included, sorted by cell key. Empty for an unplaced mover.
 * The cell set a `move_up_to` pick offers.
 */
export function moveReachAnchors(args: { state: EncounterState; moverId: string; dist: number }): Cell[] {
  const { state, moverId, dist } = args;
  const mover = state.combatants[moverId];
  if (!mover?.pos) return [];
  const world = worldFor(state);
  const reach = reachableAnchors({ space: world, from: mover.pos, size: mover.base.size, budget: dist, ctx: movementContext(world, state, moverId) });
  return [...reach.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([, r]) => r.cell);
}

/**
 * **Accelerate's loose-object redirect** (`ms_accelerate`, Long Rim: "Grenades, Deployables, or
 * other loose objects of Size 1 or smaller that are thrown or deployed into one of the spaces are
 * also pushed towards the other space before activating or detonating. They activate or detonate
 * early if they are forced to stop by another character or object."). When `cell` is a two-space
 * mark (a live area with a `pair`), the cell the object ends on: the straight line toward the pair
 * walked until the next space is off the map, solid, or occupied. `undefined` when `cell` is no mark
 * or the first step is already blocked (the object stays where it landed).
 */
export function pairRedirectCell(state: EncounterState, cell: Cell): Cell | undefined {
  const world = worldFor(state);
  const mark = Object.values(state.areaEffects ?? {}).find((a) => a.pair !== undefined && world.equals(a.origin, cell));
  if (mark === undefined) return undefined;
  const ctx = movementContext(world, state, "");
  // Any character or object stops it, whatever its side or whether it blocks movement.
  const occupied = new Set(Object.values(state.combatants).filter((c) => !hasStatus(c.statuses, "Destroyed")).flatMap((c) => footprintCells(world, c).map((f) => world.key(f))));
  let cur = cell;
  for (const next of world.line(cell, mark.pair!).slice(1)) {
    const key = world.key(next);
    if (!ctx.inBounds(next) || ctx.blocked.has(key) || occupied.has(key)) break;
    cur = next;
  }
  return world.equals(cur, cell) ? undefined : tagCell(encounterGridMode(state), cur);
}
